import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Switch } from './ui/switch';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { reassignmentChainAPI } from '../lib/api';
import {
    Loader2,
    RefreshCw,
    GripVertical,
    UserCheck,
    UserX,
    MessageSquare,
    ArrowDown,
    Save,
    Plus,
    X,
    AlertTriangle
} from 'lucide-react';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from './ui/select';

export default function ReassignmentChainSettings() {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [settings, setSettings] = useState({
        enabled: false,
        vendors: [],
        vendor_details: [],
        auto_reassign_on_unavailable: true
    });
    const [availableVendors, setAvailableVendors] = useState([]);
    const [selectedVendor, setSelectedVendor] = useState('');

    const fetchSettings = useCallback(async () => {
        try {
            setLoading(true);
            const [settingsRes, vendorsRes] = await Promise.all([
                reassignmentChainAPI.getSettings(),
                reassignmentChainAPI.getAvailableVendors()
            ]);
            setSettings(settingsRes.data);
            setAvailableVendors(vendorsRes.data);
        } catch (error) {
            console.error('Error fetching settings:', error);
            toast.error('Erreur lors du chargement des paramètres');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchSettings();
    }, [fetchSettings]);

    const handleSave = async () => {
        try {
            setSaving(true);
            await reassignmentChainAPI.updateSettings({
                enabled: settings.enabled,
                vendors: settings.vendors,
                auto_reassign_on_unavailable: settings.auto_reassign_on_unavailable
            });
            toast.success('Paramètres enregistrés!');
            fetchSettings();
        } catch (error) {
            console.error('Error saving settings:', error);
            toast.error('Erreur lors de l\'enregistrement');
        } finally {
            setSaving(false);
        }
    };

    const addVendorToChain = () => {
        if (!selectedVendor) return;
        if (settings.vendors.includes(selectedVendor)) {
            toast.error('Ce vendeur est déjà dans la chaîne');
            return;
        }
        
        const vendor = availableVendors.find(v => v.id === selectedVendor);
        setSettings({
            ...settings,
            vendors: [...settings.vendors, selectedVendor],
            vendor_details: [...settings.vendor_details, vendor]
        });
        setSelectedVendor('');
    };

    const removeVendorFromChain = (vendorId) => {
        setSettings({
            ...settings,
            vendors: settings.vendors.filter(id => id !== vendorId),
            vendor_details: settings.vendor_details.filter(v => v.id !== vendorId)
        });
    };

    const moveVendor = (index, direction) => {
        const newVendors = [...settings.vendors];
        const newDetails = [...settings.vendor_details];
        const newIndex = direction === 'up' ? index - 1 : index + 1;
        
        if (newIndex < 0 || newIndex >= newVendors.length) return;
        
        [newVendors[index], newVendors[newIndex]] = [newVendors[newIndex], newVendors[index]];
        [newDetails[index], newDetails[newIndex]] = [newDetails[newIndex], newDetails[index]];
        
        setSettings({
            ...settings,
            vendors: newVendors,
            vendor_details: newDetails
        });
    };

    // Get vendors not in the chain
    const vendorsNotInChain = availableVendors.filter(v => !settings.vendors.includes(v.id));

    if (loading) {
        return (
            <Card>
                <CardContent className="py-8 flex items-center justify-center">
                    <Loader2 className="w-6 h-6 animate-spin" />
                </CardContent>
            </Card>
        );
    }

    return (
        <Card>
            <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                    <div>
                        <CardTitle className="text-base flex items-center gap-2">
                            <RefreshCw className="w-5 h-5 text-orange-500" />
                            Chaîne de Réassignation Automatique
                        </CardTitle>
                        <CardDescription>
                            Configurez l'ordre des vendeurs pour la réassignation automatique quand un produit n'est pas disponible
                        </CardDescription>
                    </div>
                    <Button onClick={handleSave} disabled={saving} size="sm">
                        {saving ? (
                            <Loader2 className="w-4 h-4 animate-spin mr-2" />
                        ) : (
                            <Save className="w-4 h-4 mr-2" />
                        )}
                        Enregistrer
                    </Button>
                </div>
            </CardHeader>
            <CardContent className="space-y-6">
                {/* Enable/Disable Toggle */}
                <div className="flex items-center justify-between p-4 bg-orange-50 rounded-lg border border-orange-200">
                    <div>
                        <Label className="text-base font-medium">Activer la chaîne de réassignation</Label>
                        <p className="text-sm text-muted-foreground mt-1">
                            Quand un vendeur marque "Non disponible", la commande sera automatiquement transférée au vendeur suivant
                        </p>
                    </div>
                    <Switch
                        checked={settings.enabled}
                        onCheckedChange={(checked) => setSettings({ ...settings, enabled: checked })}
                    />
                </div>

                {settings.enabled && (
                    <>
                        {/* Auto reassign toggle */}
                        <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                            <div>
                                <Label className="text-sm font-medium">Réassignation automatique</Label>
                                <p className="text-xs text-muted-foreground">
                                    Transférer automatiquement au vendeur suivant
                                </p>
                            </div>
                            <Switch
                                checked={settings.auto_reassign_on_unavailable}
                                onCheckedChange={(checked) => setSettings({ ...settings, auto_reassign_on_unavailable: checked })}
                            />
                        </div>

                        {/* Add vendor to chain */}
                        <div className="flex gap-2">
                            <Select value={selectedVendor} onValueChange={setSelectedVendor}>
                                <SelectTrigger className="flex-1">
                                    <SelectValue placeholder="Sélectionner un vendeur à ajouter..." />
                                </SelectTrigger>
                                <SelectContent>
                                    {vendorsNotInChain.length === 0 ? (
                                        <SelectItem value="none" disabled>
                                            Tous les vendeurs sont dans la chaîne
                                        </SelectItem>
                                    ) : (
                                        vendorsNotInChain.map((vendor) => (
                                            <SelectItem key={vendor.id} value={vendor.id}>
                                                <div className="flex items-center gap-2">
                                                    <span>{vendor.name}</span>
                                                    {vendor.has_telegram ? (
                                                        <Badge variant="outline" className="text-xs bg-green-50">
                                                            <MessageSquare className="w-3 h-3 mr-1" />
                                                            Telegram
                                                        </Badge>
                                                    ) : (
                                                        <Badge variant="outline" className="text-xs bg-red-50">
                                                            <AlertTriangle className="w-3 h-3 mr-1" />
                                                            Pas de Telegram
                                                        </Badge>
                                                    )}
                                                </div>
                                            </SelectItem>
                                        ))
                                    )}
                                </SelectContent>
                            </Select>
                            <Button onClick={addVendorToChain} disabled={!selectedVendor}>
                                <Plus className="w-4 h-4 mr-1" />
                                Ajouter
                            </Button>
                        </div>

                        {/* Vendor Chain List */}
                        <div className="space-y-2">
                            <Label className="text-sm font-medium">
                                Ordre de la chaîne ({settings.vendor_details.length} vendeur{settings.vendor_details.length > 1 ? 's' : ''})
                            </Label>
                            
                            {settings.vendor_details.length === 0 ? (
                                <div className="p-8 border-2 border-dashed rounded-lg text-center text-muted-foreground">
                                    <UserX className="w-8 h-8 mx-auto mb-2 opacity-50" />
                                    <p>Aucun vendeur dans la chaîne</p>
                                    <p className="text-sm">Ajoutez des vendeurs pour activer la réassignation automatique</p>
                                </div>
                            ) : (
                                <div className="space-y-2">
                                    {settings.vendor_details.map((vendor, index) => (
                                        <div
                                            key={vendor.id}
                                            className="flex items-center gap-3 p-3 bg-background border rounded-lg hover:shadow-sm transition-shadow"
                                        >
                                            {/* Position */}
                                            <div className="flex flex-col items-center gap-1">
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    className="h-6 w-6 p-0"
                                                    onClick={() => moveVendor(index, 'up')}
                                                    disabled={index === 0}
                                                >
                                                    <ArrowDown className="w-3 h-3 rotate-180" />
                                                </Button>
                                                <span className="text-xs font-bold text-muted-foreground w-6 text-center">
                                                    {index + 1}
                                                </span>
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    className="h-6 w-6 p-0"
                                                    onClick={() => moveVendor(index, 'down')}
                                                    disabled={index === settings.vendor_details.length - 1}
                                                >
                                                    <ArrowDown className="w-3 h-3" />
                                                </Button>
                                            </div>

                                            {/* Drag Handle */}
                                            <GripVertical className="w-4 h-4 text-muted-foreground cursor-grab" />

                                            {/* Vendor Info */}
                                            <div className="flex-1">
                                                <div className="flex items-center gap-2">
                                                    <span className="font-medium">{vendor.name}</span>
                                                    {vendor.has_telegram ? (
                                                        <Badge variant="outline" className="text-xs bg-green-50 text-green-700">
                                                            <MessageSquare className="w-3 h-3 mr-1" />
                                                            Telegram OK
                                                        </Badge>
                                                    ) : (
                                                        <Badge variant="outline" className="text-xs bg-red-50 text-red-700">
                                                            <AlertTriangle className="w-3 h-3 mr-1" />
                                                            Pas de Telegram
                                                        </Badge>
                                                    )}
                                                    {!vendor.is_active && (
                                                        <Badge variant="outline" className="text-xs bg-gray-100">
                                                            Inactif
                                                        </Badge>
                                                    )}
                                                </div>
                                                <p className="text-xs text-muted-foreground">{vendor.email}</p>
                                            </div>

                                            {/* Remove Button */}
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                className="text-red-500 hover:text-red-700 hover:bg-red-50"
                                                onClick={() => removeVendorFromChain(vendor.id)}
                                            >
                                                <X className="w-4 h-4" />
                                            </Button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Info box */}
                        <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                            <h4 className="font-medium text-blue-800 flex items-center gap-2">
                                <UserCheck className="w-4 h-4" />
                                Comment ça fonctionne
                            </h4>
                            <ul className="mt-2 text-sm text-blue-700 space-y-1 list-disc list-inside">
                                <li>Vendeur 1 reçoit la commande → clique "Non disponible"</li>
                                <li>La commande est automatiquement envoyée au Vendeur 2</li>
                                <li>Si tous les vendeurs disent "Non disponible" → Rupture de stock</li>
                            </ul>
                        </div>
                    </>
                )}
            </CardContent>
        </Card>
    );
}
